import React, { useState, useEffect } from 'react';
import { useAuthCheck } from '../../hooks/useAuthCheck';
import { useI18n } from '../../contexts/I18nContext';
import pricingConfigService from '../../services/pricingConfigService';
import PricingPlansView from './PricingPlansView';
import api from '../../services/api';

const PricingPlans = ({ showHeader = true, showBillingToggle = true, containerClass = '' }) => {
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [plans, setPlans] = useState({ monthly: [], yearly: [], credits: [] });
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  // 移除PayPal相关状态
  // const [showPayPalModal, setShowPayPalModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('yearly');
  
  const { t, language } = useI18n();

  // 使用统一的认证检查Hook
  const { checkAuthAndExecute, showLoginModal: authShowLoginModal, handleCloseLogin } = useAuthCheck();
  
  // 移除PayPal相关的ref
  // const paypalPlanRef = useRef(null);

  // 错误状态管理
  const [error, setError] = useState(null);

  // 获取定价配置数据
  useEffect(() => {
    const fetchPricingConfigs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 获取所有定价配置
        const { monthly, yearly, credits } = await pricingConfigService.getAllPricingConfigs(language);
        
        // 设置获取到的配置数据
        setPlans({
          monthly: monthly || [],
          yearly: yearly || [],
          credits: credits || []
        });
      } catch (error) {
         console.error('Error fetching pricing configs:', error);
         setError('Failed to load pricing information. Please try again later.');
         // 移除降级处理，异常将直接抛出，不再设置空数组
       } finally {
         setLoading(false);
       }
    };
    
    fetchPricingConfigs();
  }, [language]);

  const currentPlans = plans[billingCycle];

  const resolvePaymentLocale = (currency) => {
    const finalCurrency = currency || 'USD'
    if (language === 'zh-TW') {
      return { locale: 'tc', currency: finalCurrency };
    } else {
      return { locale: 'en', currency: finalCurrency };
    }
  };

  const formatPrice = (price) => {
    // if (language === 'zh-TW') {
    //   return `¥${price}`;
    // }
    return `$${price}`;
  };

  const getButtonText = (plan, billingCycle) => {
    if (billingCycle === 'credits') {
      return t('pricing.buyNow');
    }
    
    // For monthly and yearly subscriptions
    const planNameMap = {
      'basic': t('pricing.subscribeStarter'),
      'pro': t('pricing.subscribePro'), 
      'enterprise': t('pricing.subscribePremium')
    };
    
    return planNameMap[plan.id] || `${t('pricing.subscribe')} ${plan.name}`;
  };

  const handlePlanClick = async (plan) => {
    checkAuthAndExecute(async () => {
      const currentCycle = billingCycle;
      setSelectedPlan(plan);
      setSelectedBillingCycle(currentCycle);
      await handlePaymentMethodSelect('STRIPE', plan, currentCycle, 'USD');
    });
  };

  const handleRmbPaymentClick = async (plan) => {
    checkAuthAndExecute(async () => {
      const currentCycle = billingCycle;
      setSelectedPlan(plan);
      setSelectedBillingCycle(currentCycle);
      await handlePaymentMethodSelect('STRIPE', plan, currentCycle, 'cny');
    });
  };

  const handleCryptoPaymentClick = async (plan) => {
    checkAuthAndExecute(async () => {
      const currentCycle = billingCycle;
      setSelectedPlan(plan);
      setSelectedBillingCycle(currentCycle);
      await handlePaymentMethodSelect('crypto', plan, currentCycle, 'USD');
    });
  };
 
  const handlePaymentMethodSelect = async (paymentMethod, planArg, billingCycleArg, currency) => {
    const effectivePlan = planArg || selectedPlan;
    const effectiveBillingCycle = billingCycleArg || selectedBillingCycle;
    if (!effectivePlan) return;
 
    console.log('Payment method selected:', paymentMethod, 'selectedPlan:', effectivePlan);

    if (paymentMethod === 'paypal') {
      console.log('Processing PayPal payment with plan:', selectedPlan, 'billingCycle:', selectedBillingCycle);
      
      try {
        let response, data;
        
        // 区分一次性支付和订阅支付
        if (effectiveBillingCycle === 'credits') {
          // 积分包购买 - 一次性支付，使用新的v2接口
          console.log('Creating PayPal one-time payment for credits with new v2 API');
          const locale = resolvePaymentLocale(currency);
          const paymentRequest = {
            packageId: effectivePlan.id,
            gateway: 'PAYPAL',
            ...locale
          };
          
          response = await api.post('/v2/payment/create-credit-order', paymentRequest);
          
        } else {
          // 订阅套餐 - 订阅支付，使用新的v2接口
          console.log('Creating PayPal subscription for plan with new v2 API:', selectedPlan.name);
          
          const subscriptionRequest = {
            planId: effectivePlan.id,
            gateway: 'PAYPAL',
          };
          
          response = await api.post('/v2/payment/create-subscription-order', subscriptionRequest);
        }
        
        data = response.data;
        console.log('PayPal response:', data);
        
        if (data.success && data.checkoutUrl) {
          // 直接跳转到PayPal支付页面
          window.location.href = data.checkoutUrl;
        } else {
          console.error('PayPal payment/subscription creation failed:', data.error);
          alert('Failed to create PayPal payment. Please try again.');
        }
      } catch (error) {
        console.error('Error creating PayPal payment:', error);
        alert('An error occurred while creating PayPal payment. Please try again.');
      } finally {
        // 关闭支付方式选择弹窗并重置状态
        setShowPaymentModal(false);
        setSelectedPlan(null);
        setSelectedBillingCycle('yearly');
      }
      return;
    }

    try {
      const gateway = paymentMethod === 'crypto' ? 'crypto' : 'STRIPE';
      let paymentRequest;
      
      if (effectiveBillingCycle === 'credits') {
        const locale = resolvePaymentLocale(currency);
        paymentRequest = {
          packageId: effectivePlan.id,
          gateway,
          ...locale
        };
        
        const response = await api.post('/v2/payment/create-credit-order', paymentRequest);
        
        const data = response.data;
        
        if (data.success && data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          console.error('Payment session creation failed:', data.error);
          alert('Failed to create payment session. Please try again.');
        }
      } else {
        paymentRequest = {
          planId: effectivePlan.id,
          gateway,
        };
        
        console.log('Plan object:', effectivePlan);
        console.log('Payment request data:', paymentRequest);
        console.log('Billing cycle:', effectiveBillingCycle);
        
        const response = await api.post('/v2/payment/create-subscription-order', paymentRequest);
        
        const data = response.data;
        
        if (data.success && data.checkoutUrl) {
          // Redirect to payment page
          window.location.href = data.checkoutUrl;
        } else {
          console.error('Payment session creation failed:', data.error);
          alert('Failed to create payment session. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error creating payment session:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setSelectedPlan(null);
      setSelectedBillingCycle('yearly');
    }
  };

  const handleLoginSuccess = () => {};

  return (
    <PricingPlansView
      t={t}
      containerClass={containerClass}
      language={language}
      showHeader={showHeader}
      showBillingToggle={showBillingToggle}
      loading={loading}
      error={error}
      billingCycle={billingCycle}
      setBillingCycle={setBillingCycle}
      currentPlans={currentPlans}
      handlePlanClick={handlePlanClick}
      authShowLoginModal={authShowLoginModal}
      handleCloseLogin={handleCloseLogin}
      handleLoginSuccess={handleLoginSuccess}
      showPaymentModal={showPaymentModal}
      setShowPaymentModal={setShowPaymentModal}
      selectedPlan={selectedPlan}
      setSelectedPlan={setSelectedPlan}
      selectedBillingCycle={selectedBillingCycle}
      setSelectedBillingCycle={setSelectedBillingCycle}
      handlePaymentMethodSelect={handlePaymentMethodSelect}
      formatPrice={formatPrice}
      handleRmbPaymentClick={handleRmbPaymentClick}
      handleCryptoPaymentClick={handleCryptoPaymentClick}
      getButtonText={getButtonText}
    />
  );
};

export default PricingPlans;
